import { useEffect, useMemo, useState } from "react";
import { FolderOpen, Upload, Download, Trash2, X, Search, Loader2, History, ShieldAlert } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { API_BASE_URL } from "../lib/api";
import { useAuthStore } from "../store/useAuthStore";

// Multi-Tenant Files hub (Milestone 3): every file lives under
// Brand/Tenant → Client → (Project). Downloads go through the authorized
// endpoint (short-lived presigned links); DELETION IS ADMIN-ONLY.

const fmtSize = (n: number) =>
  !n ? "—" : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const FilesHubPage = () => {
  const { authUser } = useAuthStore();
  const isAdmin = authUser?.role === "Admin";

  const [files, setFiles] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [tenantF, setTenantF] = useState("");
  const [clientF, setClientF] = useState("");
  const [projectF, setProjectF] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"files" | "activity">("files");
  const [activity, setActivity] = useState<any[]>([]);

  // Upload modal
  const [upOpen, setUpOpen] = useState(false);
  const [upClient, setUpClient] = useState("");
  const [upProject, setUpProject] = useState("");
  const [upCategory, setUpCategory] = useState("Document");
  const [upFiles, setUpFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tenantF) params.set("tenant", tenantF);
      if (clientF) params.set("client", clientF);
      if (projectF) params.set("project", projectF);
      const { data } = await axiosInstance.get(`/files/assets?${params.toString()}`);
      setFiles(data.files || []);
    } catch { toast.error("Couldn't load files"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    axiosInstance.get("/tenants").then((r) => setTenants(r.data?.tenants || [])).catch(() => {});
    axiosInstance.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    axiosInstance.get("/project").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadFiles(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tenantF, clientF, projectF]);
  useEffect(() => {
    if (tab === "activity" && isAdmin) {
      axiosInstance.get("/files/activity").then((r) => setActivity(r.data?.activity || [])).catch(() => {});
    }
  }, [tab, isAdmin]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) =>
      [f.name, f.client?.name, f.project?.name, f.tenant?.name, f.category, f.uploadedByName]
        .some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [files, search]);

  const clientProjects = (cid: string) =>
    projects.filter((p: any) => (p.assignedContact?._id || p.assignedContact) === cid);

  const download = (f: any) => window.open(`${API_BASE_URL}/files/f/${f._id}`, "_blank");

  const remove = async (f: any) => {
    if (!window.confirm(`Delete "${f.name}" permanently? This is recorded in the audit trail.`)) return;
    try {
      await axiosInstance.delete(`/files/f/${f._id}`);
      toast.success("File deleted");
      setFiles((prev) => prev.filter((x) => x._id !== f._id));
    } catch (e: any) { toast.error(e?.response?.data?.message || "Delete failed"); }
  };

  const doUpload = async () => {
    if (!upClient) return toast.error("Select the client this file belongs to");
    if (!upFiles.length) return toast.error("Choose at least one file");
    setUploading(true);
    try {
      const fd = new FormData();
      upFiles.forEach((f) => fd.append("files", f));
      fd.append("clientId", upClient);
      if (upProject) fd.append("projectId", upProject);
      fd.append("category", upCategory);
      await axiosInstance.post("/files/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — brand & ownership inherited from the client");
      setUpOpen(false); setUpFiles([]); setUpClient(""); setUpProject("");
      loadFiles();
    } catch (e: any) { toast.error(e?.response?.data?.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const selCls = "px-2.5 py-1.5 text-[13px] bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40";

  return (
    <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="text-[24px] font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <FolderOpen size={22} className="text-primary" /> Files
            </h1>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Organized by Brand → Client → Project. Downloads are authorized &amp; time-limited.
              {isAdmin ? " Deletion is admin-only." : " Only an Administrator can delete files."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button onClick={() => setTab(tab === "files" ? "activity" : "files")}
                className="px-3 py-2 rounded-lg text-[13px] font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
                <History size={15} /> {tab === "files" ? "Activity Log" : "Back to Files"}
              </button>
            )}
            <button onClick={() => setUpOpen(true)}
              className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-primary text-white hover:opacity-95 flex items-center gap-1.5">
              <Upload size={15} /> Upload Files
            </button>
          </div>
        </div>

        {tab === "activity" && isAdmin ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">When</th>
                  <th className="px-4 py-2.5 font-semibold">Action</th>
                  <th className="px-4 py-2.5 font-semibold">File</th>
                  <th className="px-4 py-2.5 font-semibold">By</th>
                  <th className="px-4 py-2.5 font-semibold">Brand</th>
                  <th className="px-4 py-2.5 font-semibold">Client / Project</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((a) => (
                  <tr key={a._id} className="border-t border-slate-100">
                    <td className="px-4 py-2 whitespace-nowrap text-slate-500">{new Date(a.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${a.action === "deleted" ? "bg-red-100 text-red-700" : a.action === "uploaded" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{a.action}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-800">{a.fileName}</td>
                    <td className="px-4 py-2">{a.userName || "—"} <span className="text-slate-400 text-[11px]">{a.userRole}</span></td>
                    <td className="px-4 py-2">{a.tenant?.name || "—"}</td>
                    <td className="px-4 py-2 text-slate-500">{a.client?.name || "—"}{a.project?.name ? ` · ${a.project.name}` : ""}</td>
                  </tr>
                ))}
                {!activity.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No activity yet.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search files, clients…"
                  className="pl-8 pr-3 py-1.5 text-[13px] bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 w-56" />
              </div>
              <select value={tenantF} onChange={(e) => setTenantF(e.target.value)} className={selCls}>
                <option value="">All Brands</option>
                {tenants.map((t: any) => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
              <select value={clientF} onChange={(e) => { setClientF(e.target.value); setProjectF(""); }} className={selCls}>
                <option value="">All Clients</option>
                {clients.map((c: any) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              <select value={projectF} onChange={(e) => setProjectF(e.target.value)} className={selCls} disabled={!clientF}>
                <option value="">{clientF ? "All Projects" : "Select client first"}</option>
                {clientProjects(clientF).map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
              <span className="text-[12px] text-slate-500 ml-auto">Files: {rows.length}</span>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">Name</th>
                    <th className="px-4 py-2.5 font-semibold">Brand</th>
                    <th className="px-4 py-2.5 font-semibold">Client</th>
                    <th className="px-4 py-2.5 font-semibold">Project</th>
                    <th className="px-4 py-2.5 font-semibold">Category</th>
                    <th className="px-4 py-2.5 font-semibold">Size</th>
                    <th className="px-4 py-2.5 font-semibold">Uploaded</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-10 text-center"><Loader2 className="animate-spin inline text-primary" /></td></tr>
                  ) : rows.map((f) => (
                    <tr key={f._id} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-4 py-2.5 font-medium text-slate-800">
                        {f.name}
                        {f.ownershipUnresolved && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-px rounded-full" title="Ownership could not be derived — needs admin review">
                            <ShieldAlert size={10} /> review
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">{f.tenant?.name || "—"}</td>
                      <td className="px-4 py-2.5">{f.client?.name || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{f.project?.name || "General"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{f.category}</td>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtSize(f.size)}</td>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(f.createdAt)}{f.uploadedByName ? ` · ${f.uploadedByName}` : ""}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => download(f)} title="Download (secure link)" className="p-1.5 rounded-lg text-primary hover:bg-primary/10"><Download size={15} /></button>
                          {isAdmin && (
                            <button onClick={() => remove(f)} title="Delete (admin only)" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && !rows.length && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No files found.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {upOpen && (
          <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setUpOpen(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h3 className="font-bold text-slate-900">Upload Files</h3>
                <button onClick={() => setUpOpen(false)} className="p-1 text-slate-400 hover:bg-slate-100 rounded-full"><X size={18} /></button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Client <span className="text-red-500">*</span> <span className="font-normal text-slate-400">(brand inherits automatically)</span></label>
                  <select value={upClient} onChange={(e) => { setUpClient(e.target.value); setUpProject(""); }} className={`${selCls} w-full`}>
                    <option value="">Select client…</option>
                    {clients.map((c: any) => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Project <span className="font-normal text-slate-400">(optional — leave empty for a general client file)</span></label>
                  <select value={upProject} onChange={(e) => setUpProject(e.target.value)} className={`${selCls} w-full`} disabled={!upClient}>
                    <option value="">— General client file —</option>
                    {clientProjects(upClient).map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Category</label>
                  <select value={upCategory} onChange={(e) => setUpCategory(e.target.value)} className={`${selCls} w-full`}>
                    {["Document", "Deliverable", "Attachment", "Client Asset", "Other"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <label className="flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-primary/50 text-slate-500 text-sm">
                  <Upload size={15} /> {upFiles.length ? `${upFiles.length} file(s) selected` : "Choose files"}
                  <input type="file" multiple className="hidden" onChange={(e) => setUpFiles(Array.from(e.target.files || []))} />
                </label>
              </div>
              <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
                <button onClick={() => setUpOpen(false)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={doUpload} disabled={uploading} className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg disabled:opacity-50">
                  {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilesHubPage;
